import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { readDrillParams } from '../utils/drilldown';
import { Search, Loader2, Flag, X, ChevronDown, Check, Download } from 'lucide-react';
import { employeesAPI, managerFocusAPI, rolesAPI, scorecardAPI, storeIntelligenceAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import type { ProjectRoleLite } from '../navigation/dashboards';
import { currentPeriodMonth, readProjectRoles } from './home/homeNumbers';
import { redFlagDefaultRole, type RedFlagRoleIntelligence } from '../utils/redFlagDefaultRole';
import { buildCsv, downloadCsv } from '../utils/csv';
import {
  buildEmployeeScoreIndex,
  formatScore,
  scoreMonthFor,
  scoreMonthLabel,
  scoreMonthShort,
  type EmployeeScoreSummary,
} from '../utils/redFlagEmployeeScore';
import EmployeeScoreHistoryModal from '../components/red-flags/EmployeeScoreHistoryModal';
import {
  LEGACY_GATED_FLAG_TYPES,
  LEGACY_GATED_THRESHOLD,
  LEGACY_GATED_THRESHOLD_RANDOM,
  gateText,
  isGatedRule,
  isLegacyRuleList,
  legacyRuleHeaderTitle,
  personalGateMeta,
  ruleHeaderTitle,
  type RedFlagTriggerMode,
  type RuleGateMeta,
} from '../utils/redFlagDisplay';
import { useRedFlagRulesEnabled } from '../components/red-flag-rules/useRedFlagRulesEnabled';
import { InlineLoader } from '../components/loading';
import { getImageViewUrl } from '../utils/imageUrl';
import TableCardWithStates from '../components/TableCardWithStates';
import ImageLightbox from '../components/ImageLightbox';

/**
 * Red Flags — verification screen. Recomputes the project's red-flag rules live
 * from source for a chosen date range (default: this month) for a manager's team,
 * a role or a single employee, ranked by recency — so a reviewer can confirm WHICH
 * employee surfaces first to the manager, and why.
 *
 * Which rules run, and how each one gates, comes from the API (`rules[]`: mode,
 * gated, ruleThreshold, kind, label). Immediate rules flag as they happen; gated
 * rules raise one flag per N occurrences in a calendar month (N per rule, or per
 * person in a month saved per role: each cell then reads that person's own N), so the
 * gated cells show the flag count. The legacy maps/constants (here and in
 * utils/redFlagDisplay) are only fallbacks for an older payload that carries labels
 * and legacy thresholds alone.
 */

// The six standard codes (plus yellow_escalation) have bespoke cell renderers; any
// other code is a custom rule (`c_<id>`) and is rendered generically from the API.
type KnownFlagType =
  | 'grooming'
  | 'uninformed_absence'
  | 'missed_training'
  | 'missing_signout'
  | 'late_signin'
  | 'missed_random'
  | 'yellow_escalation';
type RedFlagType = string;
const STANDARD_CODES: ReadonlySet<string> = new Set<KnownFlagType>([
  'grooming', 'uninformed_absence', 'missed_training', 'missing_signout', 'late_signin', 'missed_random',
]);

// How each rule gates comes from the API's rules[] (mode / gated / ruleThreshold);
// isGatedRule / ruleThresholdOf / gateText fall back to the legacy constants
// (sign-out, late every 5th; random every 10th slot) for an older payload.
/** One rule column, as GET /manager-focus/debug describes it. */
interface DebugRule extends RuleGateMeta {
  key: RedFlagType;
  label: string;
}

// Column headers for this table. We relabel a few from the engine's flag names so
// the header reads as what the cell now SHOWS (a score / a ratio / a slot) rather
// than only the failure case. Falls back to the engine label for the rest.
const COLUMN_LABELS: Partial<Record<RedFlagType, string>> = {
  grooming: 'Uniform Compliance',
  missed_training: 'Training',
  missed_random: 'Missed random',
};

interface PerType {
  flagType: RedFlagType;
  periodOccurrences: number;
  periodFlags: number;
  yearCount: number; // weighted within-CALENDAR-MONTH tally at the window's last day
  // Immediate columns: month-scoped detail.
  groomingScore?: number | null;
  groomingWorkwear?: number | null; // worst isWorkwear sub-factor over the period (0–2); <2 = flag fired
  trainingAttended?: number;
  trainingInvited?: number;
  trainingAbsent?: number; // explicitly marked absent (attended=false) — the only ones that fire a flag
  // Gated columns: red flags that crossed inside the SELECTED WINDOW + the days behind them.
  // Gated rules, scoped to the SELECTED WINDOW (a month or an arbitrary range):
  // the flags that crossed inside it, and the occurrence days behind them.
  rangeRedFlags?: number;
  rangeOccurrences?: { date: string; slots?: number[] }[];
  // Explains a window that starts mid-month: carryIn is the tally already banked
  // before it opened, so "2 days -> 1 red flag (1 per 5)" reads as intended.
  carryIn?: number;
  tallyAtEnd?: number;
  // Recency, for the per-column scope labels: the behaviour day of this rule's most
  // recent ACTIVE flag, and whether that day is today. Every cell states its own
  // scope off these, so a month-scoped column can never read as "fired today".
  lastFlagDate?: string | null;
  flaggedToday?: boolean;
  // Months saved per role only: this reportee's own N and mode. The column's rule carries
  // the parameter's smallest N, so the cell and its drill-down read these instead.
  ruleThreshold?: number;
  mode?: RedFlagTriggerMode;
}
interface Reportee {
  employeeCode: string;
  employeeName: string;
  managerCode: string | null;
  storeName: string | null;
  perType: PerType[];
  redFlags: number;
  latestFlagDate: string | null; // most-recent red-flag day — the recency the manager sees
}
interface DebugData {
  mode: 'manager' | 'employee' | 'role';
  code: string;
  projectId: number;
  // Server-resolved window. `today` is the SERVER's IST date — recency must not be
  // decided from the browser clock, which drifts for a non-IST viewer or a tab left
  // open past midnight.
  period: {
    start: string; end: string; label: string;
    month?: string | null; isWholeMonth?: boolean; includesToday?: boolean; today?: string; notice?: string;
  };
  rules: DebugRule[];
  gatedThreshold: number;
  reportees: Reportee[];
}

// One raised red flag's lifecycle (audit modal): raised → highlighted to the AM →
// marked Done (Discussed) in the app, with the resulting TAT.
interface FlagTimelineEntry {
  flagType: RedFlagType;
  label: string;
  managerCode: string | null;
  raisedDate: string;       // YYYY-MM-DD it was raised to / seen by the AM
  highlightedAt: string;    // ISO — when it surfaced (push time, else raised-day midnight)
  pushedAt: string | null;  // ISO — precise real-time push moment, when on record
  monthCount: number;
  live: boolean;            // today's flag, detected live (not yet persisted to the ledger)
  done: boolean;
  doneAt: string | null;    // ISO — when the AM marked it Done
  doneDate: string | null;  // YYYY-MM-DD — the day the AM marked Done
  tatMs: number | null;     // highlighted → done, in ms (null while open)
  // How it surfaced: real-time push / rule-detected time / 8 AM digest / fallback.
  highlightSource?: 'push' | 'detected' | 'digest' | 'deck';
}

interface PickEmp {
  code: string;
  name: string;
  teamSize?: number; // level-0 role holders: size of their team (managers only)
  hasReports?: boolean; // deeper levels: does this person manage anyone (→ can drill further)
}

// ── Role-first drill-down ─────────────────────────────────────────────────────
// A project role (iPro / AM / Trade Head). `category` (L1–L5) is the seniority
// ladder: L1 = leaf (field employees), higher = more senior manager roles.
interface RoleOpt {
  id: string;
  name: string;
  category: string;
  dataScope: string; // 'hierarchy' = a reporting tier · 'all_project' = access/admin role (not a tier)
}
// One picker level in the cascade. Level 0 = the seed role's holders; every deeper
// level = the direct reports (reporting graph) of the level above. `title` labels it.
interface CascadeLevel {
  title: string;
  members: PickEmp[];
  loading: boolean;
  sel: string[]; // [] = none selected · else one or more selected persons' codes
}
// The active trace scope that drives the table + month re-fetch.
type TraceScope =
  | { kind: 'role'; roleId: string } // all holders of a role (the leaf → all field employees)
  | { kind: 'subtree'; code: string } // a manager's whole reporting subtree
  | { kind: 'employee'; code: string }; // one employee (the separate search)

// ── Appeals (employee disputes a red flag; backend reviews + decides) ─────────
type AppealStatus = 'pending' | 'under_review' | 'cancelled' | 'rejected';
interface AppealMessage {
  id: number;
  authorType: 'employee' | 'admin';
  authorName: string | null;
  body: string;
  createdAt: string;
}
interface Appeal {
  id: number;
  projectId: number | null;
  employeeCode: string;
  employeeName: string | null;
  flagType: RedFlagType;
  eventDate: string;
  behaviourDate: string;
  details: string;
  attachmentName: string | null;
  attachmentMime: string | null;
  hasAttachment: boolean;
  status: AppealStatus;
  decisionNote: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  messages: AppealMessage[];
}
const APPEAL_STATUS: Record<AppealStatus, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  under_review: { label: 'Under review', cls: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Flag cancelled', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};
const isOpenAppeal = (s: AppealStatus): boolean => s === 'pending' || s === 'under_review';
// FALLBACK labels (the API's rule label wins): used for older payloads and for codes
// that are no longer in the loaded rule list (e.g. an appeal on a since-removed rule).
const FLAG_LABEL: Record<KnownFlagType, string> & Partial<Record<string, string>> = {
  grooming: 'Uniform not compliant',
  uninformed_absence: 'Uninformed absence',
  missed_training: 'Missed training',
  missing_signout: 'Missing sign-out',
  late_signin: 'Late sign-in',
  missed_random: 'Missed random attendance',
  yellow_escalation: 'Repeated yellow flags',
};
// Short forms for the Latest pill, which sits in a narrow column — the full
// FLAG_LABEL ("Missed random attendance") would wrap the row. These match the
// column headers so the pill points at the column it came from.
const FLAG_LABEL_SHORT: Record<KnownFlagType, string> & Partial<Record<string, string>> = {
  grooming: 'Uniform',
  uninformed_absence: 'Absence',
  missed_training: 'Training',
  missing_signout: 'Missing sign-out',
  late_signin: 'Late sign-in',
  missed_random: 'Missed random',
  yellow_escalation: 'Yellow flags',
};
/** Full label: the API rule's label → the fallback map → the code. Never "undefined". */
const fullLabel = (code: string, rule?: { label?: string | null }): string =>
  (rule?.label && String(rule.label).trim()) || FLAG_LABEL[code] || code;
/** Short label for the narrow Latest pill: the short map (standard codes) → full label. */
const shortLabel = (code: string, rule?: { label?: string | null }): string =>
  FLAG_LABEL_SHORT[code] || fullLabel(code, rule);

// ── Per-column cell rendering ────────────────────────────────────────────────
// Each rule shows something different now: grooming a score, training a ratio,
// the three gated rules a Yes/No (random names the slot) that goes red only once
// the year's tally passes the gate. `tone` drives the colour.
type CellTone = 'red' | 'amber' | 'good' | 'none';

const TONE_CLASS: Record<CellTone, string> = {
  red: 'bg-red-100 font-semibold text-red-700',
  amber: 'text-amber-700',
  good: 'text-gray-700',
  none: 'text-gray-400',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (iso: string): string => {
  const [, m, d] = iso.split('-').map((n) => Number(n));
  return `${d} ${MONTHS[(m || 1) - 1]}`;
};
// Local YYYY-MM-DD — the date pickers' value format and their `max`.
const ymdLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Default window = 1st of this month → today: exactly what the old month picker showed
// on open, so the first load is unchanged and the whole-month path stays exercised.
const defaultFrom = (): string => { const d = new Date(); return ymdLocal(new Date(d.getFullYear(), d.getMonth(), 1)); };
const defaultTo = (): string => ymdLocal(new Date());

// Audit-modal formatters.
const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};
const fmtDateOnly = (ymd: string | null): string => {
  if (!ymd) return '—';
  const [, m, d] = ymd.split('-').map((n) => Number(n));
  return d && m ? `${d} ${MONTHS[m - 1]}` : ymd;
};
// TAT (highlighted → done) as a compact "2d 4h" / "3h 20m" / "12m".
const fmtTat = (ms: number | null): string => {
  if (ms == null) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remM = mins % 60;
  if (hrs < 24) return remM ? `${hrs}h ${remM}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  const remH = hrs % 24;
  return remH ? `${days}d ${remH}h` : `${days}d`;
};

// Recency of an employee's most-recent flag day — mirrors the mobile day badge so
// the verification table can be read in the SAME order the manager's deck shows.
type RecencyKey = 'today' | 'yesterday' | 'earlier' | 'none';
const RECENCY_PILL: Record<RecencyKey, string> = {
  today: 'bg-red-100 text-red-700',
  yesterday: 'bg-amber-100 text-amber-700',
  earlier: 'bg-gray-100 text-gray-600',
  none: 'text-gray-300',
};
const recencyOf = (day: string | null, today: string, yesterday: string): { key: RecencyKey; label: string } => {
  if (!day) return { key: 'none', label: '—' };
  if (day === today) return { key: 'today', label: 'Today' };
  if (day === yesterday) return { key: 'yesterday', label: 'Yesterday' };
  return { key: 'earlier', label: fmtDay(day) };
};

// The Latest pill: WHEN the newest flag landed AND which rule raised it. Naming the
// rule is the whole point — a bare "Today" sitting beside a month-scoped column like
// "Uniform: Not compliant" reads as "uniform flag today", which is routinely wrong.
// One rule → name it; several on the same day → count them (no room for 3 names) and
// list them in the tooltip.
const latestOf = (
  latestFlagDate: string | null,
  perType: PerType[],
  today: string,
  yesterday: string,
  rulesByKey: ReadonlyMap<string, DebugRule>,
): { key: RecencyKey; label: string; title: string } => {
  const rc = recencyOf(latestFlagDate, today, yesterday);
  if (!latestFlagDate) return { ...rc, title: 'No red flag this period' };
  const hits = perType.filter((p) => p.lastFlagDate === latestFlagDate && p.periodFlags > 0);
  // Defensive: latestFlagDate is derived from the same per-type flags, so an empty
  // list shouldn't happen — fall back to the bare date rather than render "0 rules".
  if (hits.length === 0) return { ...rc, title: `Most recent red flag: ${fmtDay(latestFlagDate)}` };
  const suffix = hits.length === 1 ? shortLabel(hits[0].flagType, rulesByKey.get(hits[0].flagType)) : `${hits.length} rules`;
  return {
    ...rc,
    label: `${rc.label} · ${suffix}`,
    title: `${fmtDay(latestFlagDate)} — ${hits.map((p) => fullLabel(p.flagType, rulesByKey.get(p.flagType))).join(', ')}`,
  };
};

// Suffix a month-scoped cell with WHEN this rule last fired: "· today" while it is
// live, otherwise the date. Without it the cell states a month total with no hint of
// recency, which is the ambiguity this whole column set exists to remove.
const whenSuffix = (pt: PerType | undefined): string =>
  pt?.flaggedToday ? ' · today' : pt?.lastFlagDate ? ` · ${fmtDay(pt.lastFlagDate)}` : '';

// Generic cell for a rule without a bespoke renderer: a custom rule (`c_<id>`),
// yellow_escalation, or a standard rule whose project config changed how it gates
// (e.g. grooming set to every 3rd) — the bespoke copy would misstate those.
const describeGenericCell = (
  type: RedFlagType,
  pt: PerType | undefined,
  rule: DebugRule | undefined,
): { text: string; tone: CellTone; title: string } => {
  const occ = pt?.periodOccurrences ?? 0;
  const flags = pt?.periodFlags ?? 0;
  const label = fullLabel(type, rule);
  const last = pt?.lastFlagDate ? fmtDay(pt.lastFlagDate) : '—';
  if (isGatedRule(type, rule)) {
    const redFlags = pt?.rangeRedFlags ?? flags;
    const occs = pt?.rangeOccurrences;
    if (occs ? occs.length === 0 : occ === 0 && redFlags === 0) {
      return { text: '0', tone: 'none', title: `No ${label} occurrences in this range` };
    }
    const dayCount = occs ? occs.length : occ;
    const days = occs && occs.length ? ` Days: ${occs.map((o) => fmtDay(o.date)).join(', ')}` : '';
    // Same reading as the standard gated columns: the number is the FLAG count; amber =
    // counting, not yet a flag.
    return {
      text: `${redFlags}${whenSuffix(pt)}`,
      tone: redFlags > 0 || pt?.flaggedToday ? 'red' : 'amber',
      title: `${label}: ${dayCount} day(s) in this range → ${redFlags} red flag(s) (${gateText(type, rule)}).${days}`,
    };
  }
  if (flags === 0 && occ === 0) return { text: 'None', tone: 'none', title: `No ${label} red flag in this range` };
  // Immediate: one occurrence day is one red flag.
  const n = flags > 0 ? flags : occ;
  return {
    text: `${n}d${whenSuffix(pt)}`,
    tone: pt?.flaggedToday ? 'red' : 'amber',
    title: `${label}: ${n} flagged day(s) in this range → ${flags} red flag(s). Last: ${last}`,
  };
};

const describeCell = (
  type: RedFlagType,
  pt?: PerType,
  rule?: DebugRule,
): { text: string; tone: CellTone; title: string } => {
  const occ = pt?.periodOccurrences ?? 0;
  const flags = pt?.periodFlags ?? 0;
  // The bespoke renderers below assume a standard rule's LEGACY gating (immediate vs
  // gated). Anything else — a custom code, or a standard rule re-configured to gate
  // the other way — is rendered generically.
  if (!STANDARD_CODES.has(type) || isGatedRule(type, rule) !== LEGACY_GATED_FLAG_TYPES.has(type)) {
    return describeGenericCell(type, pt, rule);
  }
  switch (type) {
    case 'grooming': {
      // Compliance IS the grooming flag. The flag now fires on uniformStatus =
      // 'non_compliant' (with isWorkwear < 2 as a legacy fallback), the SAME signal
      // the Grooming Report uses — so read it off periodFlags rather than isWorkwear
      // alone (which mislabelled a uniformStatus-flagged, isWorkwear≥2 employee).
      // One non-compliant day = one flag, so periodFlags IS the day count. Show it with
      // WHEN it last happened, and keep red only while it's today's problem — an
      // employee who fixed their uniform two weeks ago shouldn't look live-red.
      if (flags > 0) {
        return {
          text: `${flags}d${whenSuffix(pt)}`,
          tone: pt?.flaggedToday ? 'red' : 'amber',
          title: `${flags} non-compliant uniform day(s) in this range → ${flags} red flag(s). Last: ${pt?.lastFlagDate ? fmtDay(pt.lastFlagDate) : '—'}`,
        };
      }
      // No flag: distinguish captured-and-compliant from nothing captured.
      const hasData = pt?.groomingScore != null || pt?.groomingWorkwear != null;
      if (!hasData) return { text: '—', tone: 'none', title: 'No uniform captured in this range' };
      return { text: 'Compliant', tone: 'good', title: 'Uniform compliant in this range — no red flag.' };
    }
    case 'uninformed_absence': {
      if (occ === 0) return { text: 'None', tone: 'none', title: 'No uninformed absence in this range' };
      return {
        text: `${occ}d${whenSuffix(pt)}`,
        tone: pt?.flaggedToday ? 'red' : 'amber',
        title: `${occ} uninformed-absence day(s) in this range — red flag. Last: ${pt?.lastFlagDate ? fmtDay(pt.lastFlagDate) : '—'}`,
      };
    }
    case 'missed_training': {
      const invited = pt?.trainingInvited ?? 0;
      const attended = pt?.trainingAttended ?? 0;
      // A red flag fires ONLY on an EXPLICIT absent mark (attended uploaded as No).
      // Attendance not yet uploaded (NULL) is NOT a miss — so the red/"missed" state
      // keys off `absent`, never invited−attended.
      const absent = pt?.trainingAbsent ?? 0;
      if (invited === 0) return { text: '—', tone: 'none', title: 'No training invitations in this range' };
      const notMarked = Math.max(0, invited - attended - absent);
      // Lead with the MISSED count, not the attended ratio. Only an explicit absent
      // mark raises a flag, so "1 / 3" was ambiguous by design: 2 marked absent (2
      // flags) and 2 not-yet-uploaded (0 flags) both rendered "1 / 3", separable only
      // by the cell colour. The number now IS the flag-driving one.
      const detail =
        `${absent} missed of ${invited} invited · ${attended} attended` +
        (absent > 0 ? ` — ${absent} red flag(s)` : '') +
        (notMarked > 0 ? ` · ${notMarked} not yet marked (no flag unless uploaded as No)` : '');
      // Pending is shown on the face of the cell too — without it a "0 missed" reads as
      // a clean month when most of the month's attendance simply hasn't been uploaded.
      const pending = notMarked > 0 ? ` · ${notMarked} pending` : '';
      if (absent === 0) return { text: `0 missed / ${invited}${pending}`, tone: 'good', title: detail };
      return {
        text: `${absent} missed / ${invited}${whenSuffix(pt)}`,
        tone: pt?.flaggedToday ? 'red' : 'amber',
        title: detail,
      };
    }
    case 'missing_signout':
    case 'late_signin': {
      // Scoped to the selected window: red-flag count + the days behind it. The number is
      // the FLAG count, not the day count — gating is 1 per N occurrences per calendar month.
      const noun = type === 'missing_signout' ? 'missing sign-out' : 'late sign-in';
      const redFlags = pt?.rangeRedFlags ?? 0;
      const occs = pt?.rangeOccurrences ?? [];
      if (occs.length === 0) {
        return { text: '0', tone: 'none', title: `No ${noun} in this range` };
      }
      const days = occs.map((o) => fmtDay(o.date)).join(', ');
      // Gated: the number is the FLAG count, not the day count (1 per N days). Amber
      // means "counting, not yet a flag" — the state that used to render a bare grey
      // 0 and read as "nothing happened".
      return {
        text: `${redFlags}${whenSuffix(pt)}`,
        tone: redFlags > 0 || pt?.flaggedToday ? 'red' : 'amber',
        title: `${occs.length} ${noun} day(s) in this range → ${redFlags} red flag(s) (${gateText(type, rule)}). Days: ${days}`,
      };
    }
    case 'missed_random': {
      // Scoped to the selected window. Counts SLOTS (a day can miss both).
      const redFlags = pt?.rangeRedFlags ?? 0;
      const occs = pt?.rangeOccurrences ?? [];
      const totalSlots = occs.reduce((s, o) => s + (o.slots?.length ?? 1), 0);
      if (occs.length === 0) {
        return { text: '0', tone: 'none', title: 'No missed random slots in this range' };
      }
      const detail = occs.map((o) => `${fmtDay(o.date)}: slot ${(o.slots ?? []).join(', ')}`).join('; ');
      return {
        text: `${redFlags}${whenSuffix(pt)}`,
        tone: redFlags > 0 || pt?.flaggedToday ? 'red' : 'amber',
        title: `${totalSlots} missed slot(s) over ${occs.length} day(s) in this range → ${redFlags} red flag(s) (${gateText(type, rule)}). ${detail}`,
      };
    }
    default:
      return describeGenericCell(type, pt, rule);
  }
};

// A cell is worth clicking (opens the drill-down modal) only when it actually has
// detail behind it — i.e. anything other than the empty/no-data tone.
const cellHasDetail = (tone: CellTone): boolean => tone !== 'none';

// Shape returned by GET /manager-focus/red-flag-detail (grooming captures /
// training sessions / flagged dates), fetched on demand for one clicked cell.
interface RedFlagDetailData {
  kind: 'grooming' | 'training' | 'dates';
  captures?: Array<{
    date: string; imageUrl: string | null; profilePhotoUrl: string | null;
    score: number | null; uniformScore: number | null; backgroundScore: number | null;
    uniformStatus: string | null; compliant: boolean;
    // 'no_verdict' = the photo could not be analysed (older servers send no `verdict`).
    verdict?: 'compliant' | 'non_compliant' | 'no_verdict';
  }>;
  sessions?: Array<{ date: string; name: string; status: 'attended' | 'absent' | 'not_marked' }>;
  dates?: Array<{ date: string; slots?: number[] }>;
}

// A gated rule's cell carries its occurrence days (rangeOccurrences), so its drill-down
// renders from the row itself; everything else fetches detail on open.
const rendersFromPayload = (code: string, pt: PerType | undefined, rule: DebugRule | undefined): boolean =>
  isGatedRule(code, rule) && Array.isArray(pt?.rangeOccurrences);

// Per-parameter drill-down modal, opened from a red-flag cell. The three gated,
// date-based params (sign-out / late / random) render from the row's own
// `rangeOccurrences` (already in the table payload); grooming / absence / training
// render `data` fetched on open.
function RedFlagDetailModal({
  emp, flagType, label, month, pt, rule, data, loading, onClose,
}: {
  emp: { code: string; name: string };
  flagType: RedFlagType;
  label: string;
  month: string;
  pt?: PerType;
  rule?: DebugRule;
  data: RedFlagDetailData | null;
  loading: boolean;
  onClose: () => void;
}) {
  const isClientDates = rendersFromPayload(flagType, pt, rule); // gated: the days are already in the payload
  const occs = pt?.rangeOccurrences ?? [];
  // Full-size viewer for a grooming capture. The grid thumbnails are small on purpose;
  // deciding whether a uniform call was right (footwear especially) needs the whole
  // frame, so each one opens in the shared lightbox — same component the Grooming
  // Report uses, which brings zoom, download and Escape-to-close for free.
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const Empty = ({ text }: { text: string }) => (
    <div className="py-10 text-center text-sm text-gray-500">{text}</div>
  );

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <div>
            <div className="text-base font-semibold text-gray-900">{label}</div>
            <div className="font-mono text-xs text-gray-400">{emp.name} · {emp.code} · {month}</div>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-5">
          {isClientDates ? (
            occs.length === 0 ? (
              <Empty text="No occurrences in this range." />
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-gray-500">
                  {occs.reduce((s, o) => s + (o.slots?.length ?? 1), 0)} occurrence(s) over {occs.length} day(s)
                  {' → '}
                  <span className="font-semibold text-rose-600">{pt?.rangeRedFlags ?? 0} red flag(s)</span>
                  <span className="text-gray-400"> ({gateText(flagType, rule)})</span>
                </div>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                  {occs.map((o) => (
                    <li key={o.date} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="font-medium text-gray-800">{fmtDay(o.date)}</span>
                      {o.slots?.length ? (
                        <span className="text-xs text-gray-500">slot {o.slots.join(', ')}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <InlineLoader label="Loading detail…" />
            </div>
          ) : !data ? (
            <Empty text="No detail available." />
          ) : data.kind === 'grooming' ? (
            (data.captures ?? []).length === 0 ? (
              <Empty text="No uniform captured in this range." />
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {(data.captures ?? []).map((c, i) => {
                  const src = getImageViewUrl(c.imageUrl);
                  return (
                    <div key={`${c.date}-${i}`} className={`overflow-hidden rounded-lg border ${c.compliant ? 'border-gray-200' : 'border-rose-300 ring-1 ring-rose-200'}`}>
                      {src ? (
                        // object-CONTAIN, not cover: these are full-body uniform shots and
                        // a cover crop cuts the feet off — the exact detail a footwear
                        // call turns on. Click opens the untouched frame in the lightbox.
                        <button
                          type="button"
                          onClick={() => setLightbox({ src, alt: `${emp.name} · uniform on ${fmtDay(c.date)}` })}
                          title="Click to view full size"
                          className="group block h-40 w-full cursor-zoom-in bg-gray-50"
                        >
                          <img
                            src={src}
                            alt={`Uniform on ${c.date}`}
                            // Phone selfies carry EXIF rotation; every other selfie thumbnail
                            // in the app honours it, and the lightbox does too — without this
                            // the thumbnail would sit sideways next to an upright full view.
                            style={{ imageOrientation: 'from-image' }}
                            className="h-40 w-full object-contain transition-transform duration-150 group-hover:scale-[1.03]"
                          />
                        </button>
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center bg-gray-50 text-xs text-gray-400">No image</div>
                      )}
                      <div className="space-y-1 p-2">
                        <div className="text-xs font-medium text-gray-700">{fmtDay(c.date)}</div>
                        <div className="text-xs text-gray-500">Score: <span className="font-semibold text-gray-800">{c.score == null ? '—' : `${c.score}/10`}</span></div>
                        {c.verdict === 'no_verdict' ? (
                          <span title="The photo could not be analysed" className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                            Not judged
                          </span>
                        ) : (
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${c.compliant ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                            {c.compliant ? 'Compliant' : 'Not compliant'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : data.kind === 'training' ? (
            (data.sessions ?? []).length === 0 ? (
              <Empty text="No training sessions in this range." />
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                {(data.sessions ?? []).map((s, i) => (
                  <li key={`${s.date}-${i}`} className="flex items-center justify-between px-4 py-2 text-sm">
                    <div>
                      <div className="font-medium text-gray-800">{s.name}</div>
                      <div className="text-xs text-gray-400">{fmtDay(s.date)}</div>
                    </div>
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${s.status === 'attended' ? 'bg-emerald-100 text-emerald-700' : s.status === 'absent' ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>
                      {s.status === 'attended' ? 'Attended' : s.status === 'absent' ? 'Absent (flag)' : 'Not marked'}
                    </span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            (data.dates ?? []).length === 0 ? (
              <Empty text="No flagged days in this range." />
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                {(data.dates ?? []).map((d, i) => (
                  <li key={`${d.date}-${i}`} className="px-4 py-2 text-sm font-medium text-gray-800">{fmtDay(d.date)}</li>
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </div>
    {/* SIBLING of the modal, never a child: the modal's backdrop closes on any click
        that reaches it, so nesting the lightbox inside would make its own controls
        (zoom, download, its backdrop) dismiss the modal underneath. z-[100] beats the
        modal's z-50, so it still layers on top. */}
    {lightbox && (
      <ImageLightbox src={lightbox.src} alt={lightbox.alt} isOpen onClose={() => setLightbox(null)} />
    )}
    </>
  );
}

// A compact multi-select dropdown for one cascade level: a trigger button (showing the
// selection summary) + a checkbox list. Toggling updates the summary live; the parent
// only re-traces the table when the dropdown CLOSES (onCommit), so a multi-pick doesn't
// fire a trace per checkbox.
function LevelMultiSelect({
  label,
  placeholder,
  members,
  selected,
  loading,
  onChange,
  onCommit,
}: {
  label: string;
  placeholder: string;
  members: PickEmp[];
  selected: string[];
  loading: boolean;
  onChange: (next: string[]) => void;
  onCommit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQ('');
        onCommit();
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onCommit]);

  // Client-side search over the (potentially thousands-long) member list, by name
  // or code. "Select all" / the rendered list both operate on this filtered view.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return members;
    return members.filter((m) => (m.name || '').toLowerCase().includes(needle) || m.code.toLowerCase().includes(needle));
  }, [members, q]);
  const allFilteredSelected = filtered.length > 0 && filtered.every((m) => selected.includes(m.code));

  const toggle = (code: string) =>
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);

  const summary = loading
    ? 'Loading…'
    : selected.length === 0
      ? placeholder
      : selected.length === 1
        ? members.find((m) => m.code === selected[0])?.name || selected[0]
        : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative">
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      <button
        type="button"
        disabled={loading}
        onClick={() => {
          if (open) onCommit();
          setOpen((o) => !o);
        }}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
      >
        <span className={`truncate ${selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>{summary}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 flex max-h-[60vh] w-[17rem] flex-col rounded-md border border-gray-200 bg-white shadow-lg">
          {/* Search — filters the list before you pick, so a long roster is workable. */}
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or code…"
                className="w-full rounded border border-gray-200 py-1.5 pl-7 pr-2 text-sm focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>
          {members.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-1.5">
              <button
                type="button"
                // Select all the CURRENTLY FILTERED members, merged with any existing picks.
                onClick={() => onChange([...new Set([...selected, ...filtered.map((m) => m.code)])])}
                disabled={allFilteredSelected}
                className="text-xs font-medium text-primary-600 hover:underline disabled:cursor-default disabled:text-gray-300 disabled:no-underline"
              >
                Select all ({filtered.length})
              </button>
              <button
                type="button"
                onClick={() => onChange([])}
                disabled={selected.length === 0}
                className="text-xs text-gray-500 hover:underline disabled:cursor-default disabled:text-gray-300 disabled:no-underline"
              >
                Clear selection
              </button>
            </div>
          )}
          <div className="overflow-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">No matches</div>
            ) : (
              filtered.map((m) => {
                const on = selected.includes(m.code);
                return (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => toggle(m.code)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${on ? 'bg-primary-50' : ''}`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300'
                      }`}
                    >
                      {on && <Check className="h-3 w-3" />}
                    </span>
                    <span className="truncate">
                      <span className="font-medium">{m.name || m.code}</span>{' '}
                      <span className="font-mono text-xs text-gray-500">{m.code}</span>
                      {m.teamSize != null && <span className="text-xs text-gray-400"> · {m.teamSize}</span>}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/** How many names the waiting card lists before "and N more". */
const WAITING_NAMES_SHOWN = 40;

export default function ManagerFocusDebugPage() {
  const user = useAuthStore((s) => s.user);
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  // Only decides the copy shown before anything is loaded (see legacyCopy below).
  const redFlagRulesEnabled = useRedFlagRulesEnabled();
  const isAnyAdmin = user?.adminRole === 'super_admin' || user?.adminRole === 'admin';

  // The table is scoped to a from/to window; default = 1st of this month → today.
  // DEEP LINK (dashboard drill-down): ?employeeCode= | ?managerCode= | ?roleId=, plus
  // ?startDate=&endDate=. Dates seed the inputs directly; the SCOPE is applied by the
  // one-shot effect below (it has to wait for the project, and for the project-change
  // reset that clears scopeSel, or the hydrated scope would be wiped immediately).
  const location = useLocation();
  const drill = useMemo(() => readDrillParams(location.search), [location.search]);
  const [fromDate, setFromDate] = useState<string>(() => drill.startDate || defaultFrom());
  const [toDate, setToDate] = useState<string>(() => drill.endDate || defaultTo());
  // The window actually LOADED (set on Apply), so labels never describe a range the
  // user has typed but not yet committed.
  const [loadedRange, setLoadedRange] = useState<{ from: string; to: string }>(
    () => ({ from: drill.startDate || defaultFrom(), to: drill.endDate || defaultTo() }),
  );
  // Full project roster — the source for the separate name/code employee search.
  const [employees, setEmployees] = useState<PickEmp[]>([]);
  // Role-first cascade: the project's roles, the selected entry role, and the
  // manager-role picker levels below it.
  const [roles, setRoles] = useState<RoleOpt[]>([]);
  const [roleId, setRoleId] = useState<string>('');
  const [cascade, setCascade] = useState<CascadeLevel[]>([]);

  // ── Opening role (store level) ──────────────────────────────────────────────
  // The page opens on the project's store-level role, already loaded for the default
  // window, instead of a blank page (utils/redFlagDefaultRole has the rule). Inputs: the
  // roles as the dashboards read them (raw category, no L1 fallback) and the dashboards'
  // store-intelligence summary, each tagged with the project it belongs to.
  const [rolesLite, setRolesLite] = useState<ProjectRoleLite[]>([]);
  const [rolesFor, setRolesFor] = useState<number | null>(null);
  const [intel, setIntel] = useState<{ projectId: number; data: RedFlagRoleIntelligence | null } | null>(null);
  // 'deciding' until the opening role is applied or ruled out for this arrival.
  const [defaultState, setDefaultState] = useState<'deciding' | 'done'>('deciding');
  // One "arrival" per project landed on (bumped only on a real project switch, never by
  // React strict mode's second effect pass). The opening load fires at most once per
  // arrival, and never after the person has chosen a role or an employee themselves.
  const arrivalRef = useRef(0);
  const decidedArrivalRef = useRef(-1);
  const intelAskedRef = useRef<number | null>(null);
  const drillProjectRef = useRef<number | null>(null); // project a deep link's scope was applied to
  const prevProjectRef = useRef(selectedProjectId);
  // Only the newest table request may write the table, and only the newest role's holders
  // may fill the role picker: the opening load can take a while on a large project, and an
  // answer that lands after the person moved on must not overwrite what they chose.
  const loadSeqRef = useRef(0);
  // Is the load in flight the opening one (the store-level role, loaded on arrival)?
  // Apply may take over from it: the person did not ask for it, so it must not hold the
  // button. Any later load of theirs keeps Apply disabled until it lands, as before.
  // State, not a ref: a new load while one is running changes no other state, and the
  // button has to redraw.
  const [openingLoad, setOpeningLoad] = useState(false);
  const holdersForRef = useRef('');
  /** The person chose a scope themselves: the opening role must not override it. */
  const claimArrival = () => {
    decidedArrivalRef.current = arrivalRef.current;
    setDefaultState('done');
  };
  // Separate employee search — independent of the cascade.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // The active trace scope (drives the table + month re-fetch) + its human label.
  const [scopeSel, setScopeSel] = useState<TraceScope | null>(null);
  const [scopeLabel, setScopeLabel] = useState<string>('');
  const [data, setData] = useState<DebugData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  // Default to RECENCY — the order the manager's app deck shows (most-recent flag
  // on top), so this verification table can be cross-checked against the mobile.
  const [sortKey, setSortKey] = useState<string>('latest');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Recency anchors. Prefer the SERVER's IST date (echoed on data.period.today) over
  // the browser clock, which drifts for a non-IST viewer or a tab left open past
  // midnight. When the loaded window does NOT reach today, both anchors are set to a
  // value no flag can equal, so every row falls through to an absolute date — which is
  // the whole point of a past range: 18 Jul / 17 Jul / 16 Jul, not "Today".
  const srvToday = data?.period?.today ?? ymdLocal(new Date());
  const windowHasToday = data?.period?.includesToday ?? true;
  const today = windowHasToday ? srvToday : '';
  const yesterday = useMemo(
    () => (windowHasToday ? ymdLocal(new Date(Date.parse(`${srvToday}T00:00:00`) - 86400000)) : ''),
    [windowHasToday, srvToday],
  );

  // ── Employee Score column ───────────────────────────────────────────────────
  // The score of the month BEFORE the period on screen, read once per (project, month)
  // from the stored six-month totals the Employee Score page and the Dashboard read
  // (utils/redFlagEmployeeScore has the rules). That endpoint answers only people who
  // may open the Employee Score page, and only for the rows they may see there; for
  // anyone it refuses, the column is not drawn at all.
  const scoreMonth = data?.period?.end ? scoreMonthFor(data.period.end) : null;
  const scoreKey = selectedProjectId != null && scoreMonth ? `${selectedProjectId}:${scoreMonth}` : null;
  const [scores, setScores] = useState<{
    key: string;
    state: 'loading' | 'ready' | 'error' | 'hidden';
    index?: Map<string, EmployeeScoreSummary>;
  } | null>(null);
  const [scoreEmp, setScoreEmp] = useState<{ code: string; name: string } | null>(null);
  // Every table load clears `data` first, so the month goes null and comes back: without
  // this the same scores would be fetched again on each scope change. A settled answer
  // for the same key is kept; a failed or interrupted one is asked again.
  const scoresRef = useRef(scores);
  scoresRef.current = scores;
  useEffect(() => {
    if (selectedProjectId == null || !scoreMonth) return;
    const key = `${selectedProjectId}:${scoreMonth}`;
    const prev = scoresRef.current;
    if (prev && prev.key === key && (prev.state === 'ready' || prev.state === 'hidden')) return;
    let cancelled = false;
    setScores({ key, state: 'loading' });
    scorecardAPI
      .getSixMonthScore(selectedProjectId, scoreMonth)
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data;
        setScores({ key, state: 'ready', index: buildEmployeeScoreIndex(d?.months ?? [], d?.employees ?? []) });
      })
      .catch((err: any) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setScores({ key, state: status === 401 || status === 403 ? 'hidden' : 'error' });
      });
    return () => { cancelled = true; };
  }, [selectedProjectId, scoreMonth]);
  // Drawn once the answer is in (never while it is still out, so a person without access
  // never sees the column appear and vanish). A failed read keeps the column, with dashes.
  const scoresNow = scores && scores.key === scoreKey ? scores : null;
  const showScore = !!scoresNow && (scoresNow.state === 'ready' || scoresNow.state === 'error');
  const scoreIndex = scoresNow?.state === 'ready' ? scoresNow.index ?? null : null;
  const scoreOf = (code: string): EmployeeScoreSummary | undefined => scoreIndex?.get(code.trim());

  // Audit modal — the per-flag raised → notified → discussed lifecycle + TAT for
  // one employee, opened from their "Red Flags" count.
  const [modalEmp, setModalEmp] = useState<{ code: string; name: string } | null>(null);
  const [timeline, setTimeline] = useState<FlagTimelineEntry[] | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const openTimeline = async (emp: { code: string; name: string }) => {
    setModalEmp(emp);
    setTimeline(null);
    setTimelineError(null);
    setTimelineLoading(true);
    try {
      const res = await managerFocusAPI.getFlagTimeline(emp.code, selectedProjectId ?? undefined, loadedRange);
      setTimeline((res.data?.data?.timeline || []) as FlagTimelineEntry[]);
    } catch (err: any) {
      setTimelineError(err?.response?.data?.message || 'Failed to load red-flag timeline');
    } finally {
      setTimelineLoading(false);
    }
  };
  const closeTimeline = () => {
    setModalEmp(null);
    setTimeline(null);
    setTimelineError(null);
  };

  // ── Per-parameter drill-down modal (click a red-flag cell) ───────────────────
  // Sign-out / late / random render from the row's own rangeOccurrences; grooming /
  // absence / training fetch detail from the endpoint on open.
  const [detailSel, setDetailSel] = useState<
    { emp: { code: string; name: string }; flagType: RedFlagType; label: string; pt?: PerType; rule?: DebugRule } | null
  >(null);
  const [detailData, setDetailData] = useState<RedFlagDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = (emp: { code: string; name: string }, flagType: RedFlagType, label: string, pt?: PerType, rule?: DebugRule) => {
    setDetailSel({ emp, flagType, label, pt, rule });
    setDetailData(null);
    // Gated rules render from the row's rangeOccurrences — no fetch.
    if (rendersFromPayload(flagType, pt, rule)) return;
    if (selectedProjectId == null) return;
    setDetailLoading(true);
    managerFocusAPI
      .getRedFlagDetail({ projectId: selectedProjectId, employeeCode: emp.code, flagType, from: loadedRange.from, to: loadedRange.to })
      .then((res) => setDetailData((res.data?.data || null) as RedFlagDetailData | null))
      .catch(() => setDetailData(null))
      .finally(() => setDetailLoading(false));
  };
  const closeDetail = () => {
    setDetailSel(null);
    setDetailData(null);
    setDetailLoading(false);
  };

  // ── Appeals review ──────────────────────────────────────────────────────────
  // All appeals in the project, grouped by employee_code (for the table badge +
  // the per-employee review modal). Reloaded with the table and after each action.
  const [appealsByEmp, setAppealsByEmp] = useState<Map<string, Appeal[]>>(new Map());
  const [appealEmp, setAppealEmp] = useState<{ code: string; name: string } | null>(null);
  const [appealBusy, setAppealBusy] = useState<number | null>(null);   // appealId being acted on
  const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
  const actorName = user?.fullName || undefined;

  const loadAppeals = async () => {
    const pid = selectedProjectId ?? undefined;
    try {
      const res = await managerFocusAPI.getAppeals({ projectId: pid });
      const list: Appeal[] = (res.data?.data || []) as Appeal[];
      const m = new Map<string, Appeal[]>();
      for (const a of list) {
        const arr = m.get(a.employeeCode) ?? [];
        arr.push(a);
        m.set(a.employeeCode, arr);
      }
      setAppealsByEmp(m);
    } catch {
      setAppealsByEmp(new Map());
    }
  };

  const appealReply = async (appealId: number) => {
    const body = (replyDrafts[appealId] || '').trim();
    if (!body) return;
    setAppealBusy(appealId);
    try {
      await managerFocusAPI.appealReply(appealId, body, actorName);
      setReplyDrafts((d) => ({ ...d, [appealId]: '' }));
      await loadAppeals();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to send reply');
    } finally {
      setAppealBusy(null);
    }
  };

  const decideAppeal = async (appeal: Appeal, decision: 'cancel' | 'reject') => {
    // The single reply box doubles as the decision note — no separate field needed.
    const note = (replyDrafts[appeal.id] || '').trim() || undefined;
    const verb = decision === 'cancel' ? 'cancel this red flag' : 'reject this appeal';
    if (!window.confirm(`Are you sure you want to ${verb}? This cannot be undone.`)) return;
    setAppealBusy(appeal.id);
    try {
      if (decision === 'cancel') await managerFocusAPI.cancelRedFlag(appeal.id, note, actorName);
      else await managerFocusAPI.rejectAppeal(appeal.id, note, actorName);
      setReplyDrafts((d) => ({ ...d, [appeal.id]: '' }));
      await loadAppeals();
      // A cancelled flag changes the red-flag counts — refresh the table too.
      if (decision === 'cancel' && scopeSel) await loadScope(scopeSel, loadedRange);
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to action appeal');
    } finally {
      setAppealBusy(null);
    }
  };

  const viewAppealDoc = async (appealId: number) => {
    try {
      const res = await managerFocusAPI.getAppealDocument(appealId);
      const url = res.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener');
      else alert('Attachment unavailable');
    } catch {
      alert('Could not load attachment');
    }
  };

  // Load the project's employees for the picker.
  useEffect(() => {
    if (!isAnyAdmin) return;
    (async () => {
      try {
        // Active-only (includeInactive=false) — the search must not surface departed employees.
        const res = await employeesAPI.getProjectEmployees(true, selectedProjectId ?? undefined, false);
        const d = res.data?.data || res.data || {};
        const all = [...(d.deployed || []), ...(d.undeployed || [])];
        const mapped: PickEmp[] = all
          .map((e: any) => ({
            code: String(e.emp_code || e.employeeCode || e.userName || '').trim(),
            name: String(e.emp_name || e.employee_name || e.name || '').trim(),
          }))
          .filter((e: PickEmp) => e.code);
        const seen = new Set<string>();
        const deduped = mapped.filter((e) => (seen.has(e.code) ? false : (seen.add(e.code), true)));
        deduped.sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code));
        setEmployees(deduped);
      } catch (err) {
        console.error('[red-flags-debug] failed to load employees', err);
      }
    })();
  }, [isAnyAdmin, selectedProjectId]);

  // Always-current cascade snapshot — the multi-select commits on close, so its
  // callback must read the latest cascade, not a stale render closure.
  const cascadeRef = useRef<CascadeLevel[]>([]);
  useEffect(() => {
    cascadeRef.current = cascade;
  }, [cascade]);

  // The project's role ladder — the entry roles for the drill-down. Reloaded per project.
  useEffect(() => {
    if (!isAnyAdmin) return;
    const pid = selectedProjectId ?? null;
    if (pid == null) {
      setRoles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await rolesAPI.getByProject(pid);
        if (cancelled) return;
        const list = (res.data?.data || []) as any[];
        const mapped: RoleOpt[] = list
          .map((r) => ({
            id: String(r.id || ''),
            name: String(r.name || '').trim(),
            category: String(r.category || 'L1').trim(),
            dataScope: String(r.data_scope || 'hierarchy').trim(),
          }))
          .filter((r) => r.id && r.name);
        setRoles(mapped);
        setRolesLite(readProjectRoles(list));
        setRolesFor(pid);
      } catch (err) {
        console.error('[red-flags-debug] failed to load roles', err);
        setRoles([]);
        if (!cancelled) {
          setRolesLite([]);
          setRolesFor(pid);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAnyAdmin, selectedProjectId]);

  // Reset the whole drill-down when the project changes. Only on a real switch: on the
  // first mount everything is already empty, and resetting there would let React strict
  // mode's second effect pass wipe a deep link's scope (or drop its answer) after the
  // deep-link effect below applied it. An answer still on its way for the last project is
  // dropped, and this project's opening role is decided afresh.
  useEffect(() => {
    if (prevProjectRef.current === selectedProjectId) return;
    prevProjectRef.current = selectedProjectId;
    arrivalRef.current += 1;
    loadSeqRef.current += 1;
    holdersForRef.current = '';
    setLoading(false);
    setDefaultState('deciding');
    setRoleId('');
    setCascade([]);
    setScopeSel(null);
    setScopeLabel('');
    setSearchQuery('');
    setSearchOpen(false);
    setData(null);
    // The last project's error is not this one's, and its deep link was one-shot.
    setError(null);
    drillProjectRef.current = null;
  }, [selectedProjectId]);

  // DEEP-LINK HYDRATION (one-shot). Applies the scope carried in the URL once the
  // project is known. Ordered AFTER the project-change reset effect above, which clears
  // scopeSel — running earlier would have the hydrated scope wiped on the same tick.
  // The ref makes it fire exactly once, so a later manual scope change is never undone.
  const drillAppliedRef = useRef(false);
  useEffect(() => {
    if (drillAppliedRef.current) return;
    if (!isAnyAdmin || selectedProjectId == null) return;
    const range = { from: fromDate, to: toDate };
    if (drill.employeeCode) {
      drillAppliedRef.current = true;
      setScopeSel({ kind: 'employee', code: drill.employeeCode });
      setScopeLabel(drill.employeeCode);
      loadScope({ kind: 'employee', code: drill.employeeCode }, range);
    } else if (drill.managerCode) {
      // A manager row → their whole reporting subtree (the flags counted against them).
      drillAppliedRef.current = true;
      setScopeSel({ kind: 'subtree', code: drill.managerCode });
      setScopeLabel(drill.manager || drill.managerCode);
      loadScope({ kind: 'subtree', code: drill.managerCode }, range);
    } else if (drill.roleId) {
      drillAppliedRef.current = true;
      setRoleId(drill.roleId);
      setScopeSel({ kind: 'role', roleId: drill.roleId });
      setScopeLabel('');
      loadScope({ kind: 'role', roleId: drill.roleId }, range);
    }
    if (drillAppliedRef.current) drillProjectRef.current = selectedProjectId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyAdmin, selectedProjectId, drill]);

  // The dashboards' store-intelligence summary, read only to find the store-level role
  // (same call and month as the home screen and the Dashboard, so the server answers it
  // from the summary it already keeps for them). Asked once per project; not asked when
  // a deep link already chose the scope. Ordered after the deep-link effect above.
  useEffect(() => {
    if (!isAnyAdmin || selectedProjectId == null) return;
    const pid = selectedProjectId;
    if (drillProjectRef.current === pid || intelAskedRef.current === pid) return;
    intelAskedRef.current = pid;
    storeIntelligenceAPI
      .getSummary(pid, currentPeriodMonth(), { monthEnd: true })
      .then((res) => {
        if (intelAskedRef.current === pid) setIntel({ projectId: pid, data: (res.data?.data ?? null) as RedFlagRoleIntelligence | null });
      })
      .catch(() => {
        if (intelAskedRef.current === pid) setIntel({ projectId: pid, data: null });
      });
  }, [isAnyAdmin, selectedProjectId]);

  // OPENING ROLE. Once this project's roles and summary are in, select its store-level
  // role and load that role's people for the window on screen — exactly what choosing
  // the role, ticking all of its holders and pressing Apply does (the role scope, one
  // request). At most once per arrival (the ref survives re-renders and strict mode);
  // skipped when a deep link chose the scope or the person already chose one. With no
  // store-level role the page stays empty and says to pick one.
  useEffect(() => {
    if (!isAnyAdmin || selectedProjectId == null) return;
    const pid = selectedProjectId;
    const arrival = arrivalRef.current;
    if (decidedArrivalRef.current === arrival) return;
    const decide = () => {
      decidedArrivalRef.current = arrival;
      setDefaultState('done');
    };
    if (drillProjectRef.current === pid) { decide(); return; }
    if (rolesFor !== pid || !intel || intel.projectId !== pid) return; // still loading
    decide();
    const pick = redFlagDefaultRole(rolesLite, intel.data);
    const opt = pick ? roles.find((r) => r.id === String(pick.id)) : undefined;
    if (!opt) return;
    const level0: CascadeLevel = { title: opt.name, members: [], loading: true, sel: [] };
    setRoleId(opt.id);
    setCascade([level0]);
    cascadeRef.current = [level0];
    const scope: TraceScope = { kind: 'role', roleId: opt.id };
    setScopeSel(scope);
    setScopeLabel(`${opt.name}: everyone`);
    loadScope(scope, { from: fromDate, to: toDate });
    setOpeningLoad(true); // after loadScope, which clears it for every load
    loadRoleHolders(opt.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnyAdmin, selectedProjectId, rolesFor, rolesLite, roles, intel]);

  // The role only SEEDS the cascade (level 0 = its holders); every deeper level is the
  // reporting graph, so we no longer treat roles as fixed ladder levels — that's what let
  // a stray role like "Admin" appear between tiers. List every role, ordered by seniority
  // (category L1→L5) then name, purely for a tidy dropdown.
  const rankOf = (c?: string): number => {
    const m = /^L(\d)$/i.exec((c || '').trim());
    return m ? Number(m[1]) : 99;
  };
  const rolesSorted = useMemo(
    () => [...roles].sort((a, b) => rankOf(a.category) - rankOf(b.category) || a.name.localeCompare(b.name)),
    [roles],
  );

  // Separate employee search over the full roster (name or code).
  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [] as PickEmp[];
    return employees.filter((e) => e.name.toLowerCase().includes(q) || e.code.toLowerCase().includes(q)).slice(0, 50);
  }, [employees, searchQuery]);

  // ── Trace fetch for whatever scope is active ────────────────────────────────
  const loadScope = async (scope: TraceScope, range: { from: string; to: string } = { from: fromDate, to: toDate }) => {
    // Only the newest request writes the table (see loadSeqRef).
    const seq = ++loadSeqRef.current;
    setOpeningLoad(false);
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res =
        scope.kind === 'employee'
          ? await managerFocusAPI.getDebug('employee', scope.code, selectedProjectId ?? undefined, range)
          : await managerFocusAPI.getDebugScoped({
              projectId: selectedProjectId ?? undefined,
              from: range.from,
              to: range.to,
              ...(scope.kind === 'role' ? { allowFromRole: scope.roleId } : { allowFromSubtree: scope.code }),
            });
      if (seq !== loadSeqRef.current) return;
      setData(res.data?.data || null);
      setLoadedRange({ from: range.from, to: range.to });
      loadAppeals(); // refresh the per-employee appeal badges alongside the table
    } catch (err: any) {
      if (seq !== loadSeqRef.current) return;
      setError(err?.response?.data?.message || 'Failed to load red-flag debug');
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
        setOpeningLoad(false);
      }
    }
  };
  /** Drop the table request in flight (its answer is ignored) — the scope was cleared. */
  const dropPendingLoad = () => {
    loadSeqRef.current += 1;
    setLoading(false);
    setOpeningLoad(false);
  };
  const applyScope = (scope: TraceScope, label: string) => {
    setScopeSel(scope);
    setScopeLabel(label);
    loadScope(scope, { from: fromDate, to: toDate });
  };

  // CSV export of the FULL selected scope + month — one row per employee: per-parameter
  // red-flag counts, the flag dates, the total, and appeal counts. Uses the fast
  // ledger/live export endpoint (NOT the heavy per-employee trace), so it works for a
  // whole role (thousands) even when the live table above is too large to render.
  const handleDownload = async () => {
    if (!scopeSel || selectedProjectId == null) return;
    setDownloading(true);
    setError(null);
    try {
      const scopeParams =
        scopeSel.kind === 'employee' ? { mode: 'employee' as const, employeeCode: scopeSel.code }
          : scopeSel.kind === 'role' ? { allowFromRole: scopeSel.roleId }
            : { allowFromSubtree: scopeSel.code };
      const res = await managerFocusAPI.getRedFlagExport({
        projectId: selectedProjectId ?? undefined,
        from: loadedRange.from,
        to: loadedRange.to,
        ...scopeParams,
      });
      const payload = res.data?.data as {
        label: string;
        types: Array<{ key: string; label: string }>;
        rows: Array<{
          employeeCode: string; employeeName: string; store: string;
          counts: Record<string, number>; dates: Record<string, string[]>;
          total: number; appeals: number; appealsPending: number;
        }>;
      } | null;
      if (!payload) { setError('No data to download for this scope / month.'); return; }
      // A code without a label (e.g. a custom rule on an older label source) is headed by
      // its key rather than "undefined".
      const types = (payload.types ?? []).map((t) => ({ key: t.key, label: fullLabel(t.key, t) }));
      // The Employee Score column goes in the file exactly when it is on screen.
      const withScore = showScore;
      const headers = [
        'Employee', 'Employee Code', 'Store',
        ...types.map((t) => t.label),
        'Total Red Flags', 'Flag Dates', 'Appeals', 'Appeals Pending',
        ...(withScore ? ['Employee Score', 'Score Month'] : []),
      ];
      const rows = payload.rows.map((r) => {
        const latest = withScore ? scoreOf(r.employeeCode)?.latest ?? null : null;
        return [
          r.employeeName,
          r.employeeCode,
          r.store || '',
          ...types.map((t) => String(r.counts[t.key] || 0)),
          String(r.total),
          types
            .filter((t) => (r.dates[t.key] || []).length > 0)
            .map((t) => `${t.label}: ${(r.dates[t.key] || []).join(', ')}`)
            .join(' | '),
          String(r.appeals),
          String(r.appealsPending),
          ...(withScore ? [latest ? formatScore(latest.score) : '', latest ? scoreMonthLabel(latest.month) : ''] : []),
        ];
      });
      const scopeTag = (scopeLabel || 'scope').replace(/[^\w-]+/g, '_').slice(0, 40);
      downloadCsv(`Red_Flags_${scopeTag}_${loadedRange.from}_to_${loadedRange.to}.csv`, buildCsv(headers, rows));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to build red-flag download');
    } finally {
      setDownloading(false);
    }
  };

  // Deferred: changing a date no longer re-traces on its own — the user must press
  // Apply. (The table then loads for the selected scope + this window.)
  const onFrom = (d: string) => {
    if (!d) return;
    setFromDate(d);
    // Keep the window valid while typing: a From past the current To would otherwise
    // sit in an impossible state until the user fixed To as well.
    if (toDate && d > toDate) setToDate(d);
  };
  const onTo = (d: string) => {
    if (!d) return;
    setToDate(d);
    if (fromDate && d < fromDate) setFromDate(d);
  };

  // Level 0 of the cascade = every ACTIVE holder of the seed role (the picker source).
  const loadRoleHolders = async (seedRoleId: string) => {
    const pid = selectedProjectId;
    if (pid == null) return;
    holdersForRef.current = seedRoleId;
    setCascade((prev) => prev.map((l, i) => (i === 0 ? { ...l, loading: true } : l)));
    try {
      const res = await managerFocusAPI.getRoleMembers(pid, seedRoleId);
      if (holdersForRef.current !== seedRoleId) return; // the role changed meanwhile
      const members: PickEmp[] = ((res.data?.data || []) as any[])
        .map((m) => ({ code: String(m.code || '').trim(), name: String(m.name || '').trim(), teamSize: m.teamSize }))
        .filter((m) => m.code);
      setCascade((prev) => prev.map((l, i) => (i === 0 ? { ...l, members, loading: false } : l)));
    } catch (err) {
      if (holdersForRef.current !== seedRoleId) return;
      console.error('[red-flags-debug] failed to load role holders', err);
      setCascade((prev) => prev.map((l, i) => (i === 0 ? { ...l, members: [], loading: false } : l)));
    }
  };

  // A deeper cascade level = the UNION of the direct reports (reporting graph) of the
  // people selected one level up. Each report's `hasReports` says whether it can drill
  // further. Fills the level already appended at `levelIdx` by onLevelChange.
  const loadDirectReports = async (levelIdx: number, parentCodes: string) => {
    const pid = selectedProjectId;
    if (pid == null) return;
    setCascade((prev) => prev.map((l, i) => (i === levelIdx ? { ...l, loading: true } : l)));
    try {
      const res = await managerFocusAPI.getDirectReports(pid, parentCodes);
      const members: PickEmp[] = ((res.data?.data || []) as any[])
        .map((m) => ({
          code: String(m.code || '').trim(),
          name: String(m.name || '').trim(),
          hasReports: !!m.hasReports,
        }))
        .filter((m) => m.code);
      setCascade((prev) => prev.map((l, i) => (i === levelIdx ? { ...l, members, loading: false } : l)));
    } catch (err) {
      console.error('[red-flags-debug] failed to load direct reports', err);
      setCascade((prev) => prev.map((l, i) => (i === levelIdx ? { ...l, members: [], loading: false } : l)));
    }
  };

  const nameOfMember = (levels: CascadeLevel[], code: string): string => {
    for (const l of levels) {
      const m = l.members.find((x) => x.code === code);
      if (m) return m.name || code;
    }
    return code;
  };

  // Terminal scope = the UNION of the DEEPEST selected level's picks' subtrees; if the
  // seed (level-0) role has no pick yet, nothing is traced (avoids a whole-project load).
  const applyCascadeScope = (levels: CascadeLevel[]) => {
    let deepest: CascadeLevel | null = null;
    let deepestIdx = -1;
    for (let i = 0; i < levels.length; i++) if (levels[i].sel.length > 0) { deepest = levels[i]; deepestIdx = i; }
    if (!deepest) {
      dropPendingLoad();
      setScopeSel(null);
      setData(null);
      setScopeLabel(`${levels[0]?.title ?? 'Role'} — pick one or more to see their team`);
      return;
    }
    const codes = deepest.sel;
    const label =
      codes.length === 1
        ? `${deepest.title}: ${nameOfMember(levels, codes[0])}`
        : `${deepest.title}: ${codes.length} selected`;
    // Level 0 = the seed role's holders. When EVERY holder is selected, scope by the
    // ROLE id (resolved server-side in ONE SQL query) instead of listing all holder
    // codes: a whole-role code list is thousands of codes joined into the request URL,
    // which both overflows the URL length limit AND makes the backend expand each code
    // through the reporting graph (an uncached CRS call per code for includeInactive) —
    // that is what makes the whole-role selection fail to load. The role path avoids both.
    // A partial pick (or any deeper drill level) keeps the explicit-code subtree path.
    if (deepestIdx === 0 && roleId && deepest.members.length > 0 && codes.length === deepest.members.length) {
      applyScope({ kind: 'role', roleId }, label);
    } else {
      applyScope({ kind: 'subtree', code: codes.join(',') }, label);
    }
  };

  // Choose the seed role. Level 0 of the cascade becomes that role's holders; deeper
  // levels are grown on demand from the reporting graph as the user drills into reports
  // (no heavy whole-project auto-load — nothing is traced until a person is picked).
  const onSelectRole = (id: string) => {
    claimArrival();
    // A table still loading for the previous scope is dropped: nothing is traced for the
    // new role until a person is picked and Apply is pressed, as before.
    dropPendingLoad();
    setRoleId(id);
    setSearchQuery('');
    setSearchOpen(false);
    if (!id) {
      holdersForRef.current = '';
      setCascade([]);
      setScopeSel(null);
      setScopeLabel('');
      setData(null);
      return;
    }
    const entry = roles.find((r) => r.id === id) || null;
    const title = entry?.name ?? 'Role';
    const level0: CascadeLevel = { title, members: [], loading: true, sel: [] };
    setCascade([level0]);
    cascadeRef.current = [level0];
    setScopeSel(null);
    setData(null);
    setScopeLabel(`${title} — pick one or more to see their team`);
    loadRoleHolders(id);
  };

  // Does this member manage anyone (→ a further drill level is worth offering)? Deeper
  // levels carry `hasReports` straight from the graph; level-0 role holders only carry
  // `teamSize`, so fall back to that.
  const memberHasReports = (m?: PickEmp): boolean =>
    m ? (m.hasReports ?? (m.teamSize != null && m.teamSize > 0)) : false;

  // Label a drill level by whose reports it holds.
  const drillTitle = (parent: CascadeLevel, codes: string[]): string =>
    codes.length === 1
      ? `Reports of ${parent.members.find((m) => m.code === codes[0])?.name || codes[0]}`
      : `Reports of ${codes.length} selected`;

  // Toggle selections at cascade level `idx`: set this level's picks, DROP every deeper
  // level (they hung off the old pick), then append + load the next drill level iff any
  // selected person manages people. The table re-traces on close (commit), not per click.
  const onLevelChange = (idx: number, codes: string[]) => {
    const base = cascadeRef.current;
    let next: CascadeLevel[] = base.slice(0, idx + 1).map((l, i) => (i === idx ? { ...l, sel: codes } : l));
    const canDrill = codes.some((c) => memberHasReports(next[idx].members.find((m) => m.code === c)));
    if (canDrill) next = [...next, { title: drillTitle(next[idx], codes), members: [], loading: true, sel: [] }];
    setCascade(next);
    cascadeRef.current = next;
    if (canDrill) loadDirectReports(idx + 1, codes.join(','));
  };
  // Deferred load: picking in a dropdown only updates the pending selection (and
  // grows the next drill level) — it no longer traces the table on close. The
  // trace runs only when the user presses Apply.
  const onLevelCommit = () => {};
  // Apply — (re)load the table for the current cascade selection + month. Falls
  // back to re-running an active employee-search scope (e.g. after a month change).
  const onApply = () => {
    const levels = cascadeRef.current;
    if (levels.some((l) => l.sel.length > 0)) applyCascadeScope(levels);
    else if (scopeSel) loadScope(scopeSel, { from: fromDate, to: toDate });
    else applyCascadeScope(levels);
  };

  // Separate search: jump straight to one employee's flags.
  const onSearchPick = (e: PickEmp) => {
    claimArrival();
    setSearchQuery(e.name || e.code);
    setSearchOpen(false);
    setRoleId('');
    holdersForRef.current = '';
    setCascade([]);
    applyScope({ kind: 'employee', code: e.code }, `Employee: ${e.name || e.code}`);
  };

  const onSort = (key: string) =>
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setSortDir('desc');
      return key;
    });

  const sortedReportees = useMemo(() => {
    const arr = [...(data?.reportees ?? [])];
    if (sortKey === 'latest') {
      // Recency: most-recent flag day first (ties → more flags, then name) — the
      // exact order the manager's app deck surfaces.
      arr.sort((a, b) => {
        const c =
          (b.latestFlagDate || '').localeCompare(a.latestFlagDate || '') ||
          b.redFlags - a.redFlags ||
          a.employeeName.localeCompare(b.employeeName);
        return sortDir === 'asc' ? -c : c;
      });
      return arr;
    }
    if (sortKey === 'score') {
      // People without a score sit at the bottom whichever way the column is sorted.
      const sc = (r: Reportee): number | null => scoreIndex?.get(r.employeeCode.trim())?.latest?.score ?? null;
      arr.sort((a, b) => {
        const x = sc(a);
        const y = sc(b);
        if (x == null || y == null) return x == null && y == null ? 0 : x == null ? 1 : -1;
        return sortDir === 'asc' ? x - y : y - x;
      });
      return arr;
    }
    const val = (r: Reportee): number => {
      if (sortKey === 'redflags') return r.redFlags;
      const pt = r.perType.find((p) => p.flagType === sortKey);
      return pt ? pt.periodFlags : 0;
    };
    arr.sort((a, b) => (sortDir === 'asc' ? val(a) - val(b) : val(b) - val(a)));
    return arr;
  }, [data, sortKey, sortDir, scoreIndex]);

  // The loaded rule columns by code — the API's labels / gating for every lookup
  // (Latest pill, appeal cards) that starts from a bare flag code.
  const rulesByKey = useMemo(
    () => new Map<string, DebugRule>((data?.rules ?? []).map((r) => [r.key, r])),
    [data],
  );

  // Recency breakdown for the banner summary.
  const recencyCounts = useMemo(() => {
    const out = { today: 0, yesterday: 0, earlier: 0 };
    for (const r of data?.reportees ?? []) {
      if (r.redFlags <= 0 || !r.latestFlagDate) continue;
      const k = recencyOf(r.latestFlagDate, today, yesterday).key;
      if (k === 'today') out.today += 1;
      else if (k === 'yesterday') out.yesterday += 1;
      else out.earlier += 1;
    }
    return out;
  }, [data, today, yesterday]);

  // For a window that never reaches today, "N today / N yesterday" is always 0/0 and
  // tells the reader nothing. Bucket by actual flag day instead and surface the busiest
  // few, so the summary still answers "when did this happen".
  const dayBuckets = useMemo(() => {
    if (windowHasToday) return [] as { day: string; n: number }[];
    const by = new Map<string, number>();
    for (const r of data?.reportees ?? []) {
      if (r.redFlags <= 0 || !r.latestFlagDate) continue;
      by.set(r.latestFlagDate, (by.get(r.latestFlagDate) ?? 0) + 1);
    }
    return [...by.entries()]
      .map(([day, n]) => ({ day, n }))
      .sort((a, b) => b.day.localeCompare(a.day));
  }, [data, windowHasToday]);

  if (!isAnyAdmin) {
    return <div className="p-6 text-red-600">Access denied — admin only.</div>;
  }

  const flaggedCount = data ? data.reportees.filter((r) => r.redFlags > 0).length : 0;

  // The whole selected role is being traced (the opening load, or every holder ticked):
  // its people are already known from the role picker, so they are listed at once and
  // the red-flag table replaces the list when the recompute lands.
  const wholeRole = scopeSel?.kind === 'role' && scopeSel.roleId === roleId;
  const waitingPeople = wholeRole && loading && cascade[0] && !cascade[0].loading ? cascade[0].members : [];
  // Nothing chosen and nothing on its way: say so in one sentence (the opening role is
  // still being worked out → a loader instead, so the sentence never flashes first).
  const pageEmpty = selectedProjectId != null && !scopeSel && !scopeLabel && !loading && !data && !error;

  // The built-in six at their built-in gating (every project while Red Flag Rules is
  // dark, or an older payload) keep the page's original intro, legend and header
  // tooltips word for word. Before anything has loaded that is the copy too, unless the
  // server reports the feature switched on.
  const legacyCopy = data ? isLegacyRuleList(data.rules) : !redFlagRulesEnabled;
  // Otherwise the copy comes from the LOADED rules (the API's gating and N), so every
  // number on the page agrees with the column headers and cell tooltips. Before anything
  // has loaded, no rule-specific number is claimed.
  const loadedRules = data?.rules ?? [];
  const gatedRules = loadedRules.filter((r) => isGatedRule(r.key, r));
  const immediateRules = loadedRules.filter((r) => !isGatedRule(r.key, r));
  const columnLabel = (r: DebugRule): string => COLUMN_LABELS[r.key] ?? fullLabel(r.key, r);
  const slotNote = (r: DebugRule): string => (r.key === 'missed_random' ? ', each missed slot counts' : '');
  const gatedSummary = gatedRules.map((r) => `${fullLabel(r.key, r)} (${gateText(r.key, r)}${slotNote(r)})`).join(', ');
  // Immediate columns that show flagged DAYS (Training has its own missed/invited cell).
  const dayColumns = immediateRules.filter((r) => r.key !== 'missed_training');
  const hasTrainingColumn = immediateRules.some((r) => r.key === 'missed_training');
  const boldList = (names: string[]) =>
    names.map((n, i) => (
      <span key={`${n}-${i}`}>
        {i > 0 ? (i === names.length - 1 ? ' and ' : ', ') : ''}
        <span className="font-semibold">{n}</span>
      </span>
    ));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Red Flags</h1>
        {legacyCopy ? (
          <p className="mt-1 text-sm text-gray-500">
            The 6 red-flag rules recomputed live from source for a chosen <span className="font-medium">date range</span>,
            ordered by <span className="font-medium">most-recent flag</span> first — the exact order the manager's app deck
            shows, so the two can be cross-checked. Gated rules raise one red flag for every {LEGACY_GATED_THRESHOLD} occurrences
            (sign-out, late sign-in) and every {LEGACY_GATED_THRESHOLD_RANDOM} for missed random attendance (each missed slot
            counts) — that tally runs per <span className="font-medium">calendar month</span> and resets on the 1st, so a
            range starting mid-month can contain a flag whose earlier occurrences fall before it. The immediate rules
            (grooming, absence, training) flag as they happen.
          </p>
        ) : (
          <p className="mt-1 text-sm text-gray-500">
            The project's red-flag rules recomputed live from source for a chosen <span className="font-medium">date range</span>,
            ordered by <span className="font-medium">most-recent flag</span> first — the exact order the manager's app deck
            shows, so the two can be cross-checked.{' '}
            {data && loadedRules.length === 0 ? (
              'No red-flag rules are in force for this project in this range.'
            ) : (
              <>
                {!data ? (
                  <>Gated rules <span className="font-mono">⚑</span> count occurrences before they flag (how many is set per rule — hover a column header). </>
                ) : gatedRules.length > 0 ? (
                  <>Gated rules count occurrences before they flag — {gatedSummary}. </>
                ) : null}
                {(!data || gatedRules.length > 0) && (
                  <>
                    That tally runs per <span className="font-medium">calendar month</span> and resets on the 1st, so a
                    range starting mid-month can contain a flag whose earlier occurrences fall before it.{' '}
                  </>
                )}
                {!data
                  ? 'The immediate rules flag as they happen.'
                  : immediateRules.length > 0
                    ? `The immediate rules (${immediateRules.map((r) => fullLabel(r.key, r)).join(', ')}) flag as they happen.`
                    : null}
              </>
            )}
          </p>
        )}
      </div>

      {/* Filter bar: role-first cascade (left) · a separate employee name/code search
          + month (far right). Pick a role → the pickers below it appear; leave a level
          on "All" to see everyone below it. The search jumps straight to one employee. */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Entry role. */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Role</label>
            <select
              value={roleId}
              onChange={(e) => onSelectRole(e.target.value)}
              className="min-w-[11rem] rounded-md border border-gray-300 px-2 py-2 text-sm focus:border-primary-500 focus:outline-none"
            >
              <option value="">— Select role —</option>
              {rolesSorted.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          {/* Cascade pickers (multi-select) — level 0 = the seed role's holders, each
              deeper level = the direct reports of the level above (grown on demand). */}
          {cascade.map((lvl, i) => (
            <div key={`${i}:${lvl.title}`} className="w-[13rem]">
              <LevelMultiSelect
                label={lvl.title}
                placeholder={i === 0 ? (wholeRole ? 'Everyone' : `Select ${lvl.title}(s)`) : 'Select…'}
                members={lvl.members}
                selected={lvl.sel}
                loading={lvl.loading}
                onChange={(codes) => onLevelChange(i, codes)}
                onCommit={onLevelCommit}
              />
            </div>
          ))}

          {/* Separate employee search + month — pushed to the far right. */}
          <div className="ml-auto flex flex-wrap items-end gap-3">
            <div className="w-[15rem]">
              <label className="mb-1 block text-xs font-medium text-gray-500">Find employee</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => window.setTimeout(() => setSearchOpen(false), 150)}
                  placeholder="Name or code…"
                  className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none"
                />
                {searchOpen && searchQuery.trim() && (
                  <div className="absolute right-0 top-full z-20 mt-1 max-h-[60vh] w-[20rem] overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {filteredEmployees.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500">No matches</div>
                    ) : (
                      filteredEmployees.map((e) => (
                        <button
                          key={e.code}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => onSearchPick(e)}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                        >
                          <span className="font-medium">{e.name || e.code}</span>{' '}
                          <span className="font-mono text-xs text-gray-500">{e.code}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-gray-500">From</span>
              <input
                type="date"
                value={fromDate}
                max={toDate || ymdLocal(new Date())}
                onChange={(e) => onFrom(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1.5 focus:border-primary-500 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-gray-500">To</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={ymdLocal(new Date())}
                onChange={(e) => onTo(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1.5 focus:border-primary-500 focus:outline-none"
              />
            </label>

            {/* Explicit Apply — nothing loads until the user commits their filter
                choices (role / cascade picks / dates), so the table never re-traces
                live per checkbox or date tweak. */}
            <button
              type="button"
              onClick={onApply}
              // The opening load (nobody asked for it) never holds Apply; a load the
              // person started does, until it lands.
              disabled={loading && !openingLoad}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Apply
            </button>
          </div>
        </div>

        {/* Scope summary / breadcrumb. */}
        {scopeLabel && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
            {scopeSel ? (
              <>
                <span className="text-gray-400">Showing</span>
                <span className="font-medium text-gray-900">{scopeLabel}</span>
                {data && (
                  <span className="text-gray-400">
                    · {data.period.label} · {data.reportees.length} in scope · {flaggedCount} flagged
                  </span>
                )}
                {/* Full-scope CSV export — works even when the live table above is too
                    large to render (e.g. the whole iPro role), since it uses the fast
                    ledger/live export, not the per-employee trace. */}
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  title="Download every employee's red-flag breakdown (counts + dates per parameter + appeals) for this scope and month"
                  className="ml-auto inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                >
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  {downloading ? 'Preparing…' : 'Download'}
                </button>
              </>
            ) : (
              <span className="text-gray-500">{scopeLabel}</span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
          {scopeSel && (
            <span className="mt-1 block text-red-600">
              This scope may be too large to recompute live. Use <span className="font-medium">Download</span> above for the full CSV export, or drill down to a Trade Head / AM to view the live table.
            </span>
          )}
        </div>
      )}

      {scopeSel && loading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
          <InlineLoader label="Recomputing the red-flag rules from source… this can take a while for large projects." />
        </div>
      )}

      {waitingPeople.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h2 className="mb-2 text-lg font-semibold text-gray-900">
            {waitingPeople.length.toLocaleString()} {waitingPeople.length === 1 ? 'person' : 'people'} in {cascade[0].title}
          </h2>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {/* A name list, not a wall: the first ones, then how many more. */}
            {waitingPeople.slice(0, WAITING_NAMES_SHOWN).map((m) => (
              <li key={m.code} className="truncate">
                <span className="font-medium text-gray-900">{m.name || m.code}</span>{' '}
                <span className="font-mono text-[10px] text-gray-400">{m.code}</span>
              </li>
            ))}
          </ul>
          {waitingPeople.length > WAITING_NAMES_SHOWN && (
            <p className="mt-2 text-xs text-gray-500">
              and {(waitingPeople.length - WAITING_NAMES_SHOWN).toLocaleString()} more, with their red flags once they are worked out
            </p>
          )}
        </div>
      )}

      {pageEmpty && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-500">
          {defaultState === 'deciding' ? <InlineLoader label="Loading…" /> : 'Pick a role to see its people.'}
        </div>
      )}

      {scopeSel && !loading && data && (
        <>
          <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-red-800">
              <Flag className="h-4 w-4" />
              {flaggedCount} {flaggedCount === 1 ? 'employee' : 'employees'} red-flagged in {data.period.label} —
              ordered by most-recent flag (the order the manager sees).
            </div>
            <div className="mt-1.5 flex flex-wrap gap-2 pl-6 text-xs">
              {windowHasToday ? (
                <>
                  <span className="rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-700">{recencyCounts.today} today</span>
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700">{recencyCounts.yesterday} yesterday</span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 font-semibold text-gray-600">{recencyCounts.earlier} earlier in range</span>
                </>
              ) : (
                <>
                  {dayBuckets.slice(0, 3).map((b, i) => (
                    <span
                      key={b.day}
                      className={`rounded px-1.5 py-0.5 font-semibold ${i === 0 ? 'bg-red-100 text-red-700' : i === 1 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {b.n} on {fmtDay(b.day)}
                    </span>
                  ))}
                  {dayBuckets.length > 3 && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-semibold text-gray-600">
                      {dayBuckets.slice(3).reduce((t, b) => t + b.n, 0)} earlier
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Per-employee red flags — {data.period.label}</h2>
              <span className="text-xs text-gray-400">click a column to sort</span>
            </div>
            <p className="mb-2 text-xs text-gray-500">
              All columns reflect <span className="font-semibold">{data.period.label}</span>, and each states its own
              recency — <span className="font-mono">· today</span> means that rule fired today, otherwise the date is
              the last day it did. Red = fired today or already a flag; amber = flagged earlier this period, or
              counting but not yet a flag.{' '}
              <span className="font-semibold">Latest</span> names the rule behind the most recent flag (or counts them
              when several landed the same day).{' '}
              {legacyCopy ? (
                <>
                  <span className="font-semibold">Uniform</span> and <span className="font-semibold">Absence</span> show the
                  number of flagged <span className="font-semibold">days</span> — one bad day is one flag.{' '}
                  <span className="font-semibold">Training</span> is missed / invited — a flag fires only on attendance
                  uploaded as <span className="font-semibold">No</span>, so invitees whose attendance hasn&apos;t been
                  uploaded yet show as <span className="font-semibold">pending</span> and raise nothing; the gated rules <span className="font-mono">⚑</span> (sign-out, late, random) show the{' '}
                  <span className="font-semibold">red-flag count</span>, not the day count — 1 flag per {LEGACY_GATED_THRESHOLD}{' '}
                  occurrences, or per {LEGACY_GATED_THRESHOLD_RANDOM} for random (which counts each missed slot). Hover any cell
                  for the days behind it.
                </>
              ) : (
                <>
                  {dayColumns.length > 0 && (
                    <>
                      {boldList(dayColumns.map(columnLabel))} {dayColumns.length === 1 ? 'shows' : 'show'} the number of
                      flagged <span className="font-semibold">days</span> — one bad day is one flag.{' '}
                    </>
                  )}
                  {hasTrainingColumn && (
                    <>
                      <span className="font-semibold">Training</span> is missed / invited — a flag fires only on attendance
                      uploaded as <span className="font-semibold">No</span>, so invitees whose attendance hasn&apos;t been
                      uploaded yet show as <span className="font-semibold">pending</span> and raise nothing.{' '}
                    </>
                  )}
                  {gatedRules.length > 0 && (
                    <>
                      The gated rules <span className="font-mono">⚑</span> ({gatedRules.map(columnLabel).join(', ')}) show the{' '}
                      <span className="font-semibold">red-flag count</span>, not the day count —{' '}
                      {gatedRules.map((r) => `${columnLabel(r)}: ${gateText(r.key, r)}${slotNote(r)}`).join('; ')}.{' '}
                    </>
                  )}
                  Hover any cell for the days behind it.
                </>
              )}
            </p>
            <TableCardWithStates loading={false} isEmpty={data.reportees.length === 0} emptyMessage="No reportees found.">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-semibold">Employee</th>
                    <th
                      onClick={() => onSort('latest')}
                      className="cursor-pointer select-none px-2 py-2 text-center font-semibold hover:bg-gray-100"
                      title="Most recent red-flag day — the order the manager sees"
                    >
                      Latest{sortKey === 'latest' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    {(data.rules ?? []).map((r) => (
                      <th
                        key={r.key}
                        onClick={() => onSort(r.key)}
                        className="cursor-pointer select-none px-2 py-2 text-center font-semibold hover:bg-gray-100"
                        title={legacyCopy ? legacyRuleHeaderTitle(r.key, r) : ruleHeaderTitle(r.key, r)}
                      >
                        {COLUMN_LABELS[r.key] ?? fullLabel(r.key, r)}
                        {isGatedRule(r.key, r) ? ' ⚑' : ''}
                        {sortKey === r.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                    <th
                      onClick={() => onSort('redflags')}
                      className="cursor-pointer select-none px-2 py-2 text-center font-semibold hover:bg-gray-100"
                    >
                      Red Flags{sortKey === 'redflags' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                    <th className="px-2 py-2 text-center font-semibold" title="Appeals the employee has raised on their red flags">
                      Appeals
                    </th>
                    {showScore && scoreMonth && (
                      <th
                        onClick={() => onSort('score')}
                        className="cursor-pointer select-none px-2 py-2 text-center font-semibold hover:bg-gray-100"
                        title={`Employee Score for ${scoreMonthLabel(scoreMonth)}, the month before this period. Click a score for the last 6 months.`}
                      >
                        Employee Score{sortKey === 'score' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                        <div className="text-[10px] font-normal text-gray-400">{scoreMonthLabel(scoreMonth)}</div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedReportees.map((r, idx) => (
                    <tr key={r.employeeCode} className="hover:bg-gray-50">
                      <td className="sticky left-0 z-10 bg-white px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-5 text-right font-mono text-[10px] text-gray-400">#{idx + 1}</span>
                          <div>
                            <div className="font-medium text-gray-900">{r.employeeName}</div>
                            <div className="font-mono text-[10px] text-gray-400">
                              {r.employeeCode}
                              {r.storeName ? ` · ${r.storeName}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {(() => {
                          const rc = latestOf(r.latestFlagDate, r.perType, today, yesterday, rulesByKey);
                          return (
                            <span
                              title={rc.title}
                              className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 font-semibold ${RECENCY_PILL[rc.key]}`}
                            >
                              {rc.label}
                            </span>
                          );
                        })()}
                      </td>
                      {(data.rules ?? []).map((rule) => {
                        const pt = r.perType.find((p) => p.flagType === rule.key);
                        // The rule as it gates for this reportee (their own N in a month saved
                        // per role; otherwise the column's rule itself).
                        const own = personalGateMeta(rule, pt) ?? rule;
                        const cell = describeCell(rule.key, pt, own);
                        const clickable = cellHasDetail(cell.tone);
                        return (
                          <td key={rule.key} className="px-2 py-1.5 text-center">
                            {clickable ? (
                              <button
                                type="button"
                                onClick={() => openDetail({ code: r.employeeCode, name: r.employeeName }, rule.key, fullLabel(rule.key, rule), pt, own)}
                                title={`${cell.title} · Click for details`}
                                className={`inline-block min-w-[3rem] cursor-pointer whitespace-nowrap rounded px-1.5 py-0.5 hover:ring-2 hover:ring-primary-300 ${TONE_CLASS[cell.tone]}`}
                              >
                                {cell.text}
                              </button>
                            ) : (
                              <span
                                title={cell.title}
                                className={`inline-block min-w-[3rem] whitespace-nowrap rounded px-1.5 py-0.5 ${TONE_CLASS[cell.tone]}`}
                              >
                                {cell.text}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-center">
                        {r.redFlags > 0 ? (
                          <button
                            type="button"
                            onClick={() => openTimeline({ code: r.employeeCode, name: r.employeeName })}
                            title="See when each red flag was raised, highlighted to the AM, and marked Done — with TAT"
                            className="inline-block min-w-[2.5rem] cursor-pointer rounded bg-red-600 px-1.5 py-0.5 font-semibold text-white hover:bg-red-700"
                          >
                            {r.redFlags}
                          </button>
                        ) : (
                          <span className="inline-block min-w-[2.5rem] rounded px-1.5 py-0.5 font-semibold text-gray-300">
                            {r.redFlags}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {(() => {
                          const list = appealsByEmp.get(r.employeeCode) || [];
                          if (!list.length) return <span className="text-gray-300">—</span>;
                          const open = list.filter((a) => isOpenAppeal(a.status)).length;
                          return (
                            <button
                              type="button"
                              onClick={() => setAppealEmp({ code: r.employeeCode, name: r.employeeName })}
                              title="Review this employee's appeals"
                              className={`inline-block min-w-[2.5rem] cursor-pointer rounded px-1.5 py-0.5 font-semibold ${open > 0 ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            >
                              {open > 0 ? `${open} open` : list.length}
                            </button>
                          );
                        })()}
                      </td>
                      {showScore && (
                        <td className="px-2 py-1.5 text-center">
                          {(() => {
                            const s = scoreOf(r.employeeCode);
                            if (!s?.latest) {
                              return (
                                <span
                                  className="text-gray-300"
                                  title={scoresNow?.state === 'error' ? 'Scores could not be loaded' : 'No score in the last 6 months'}
                                >
                                  —
                                </span>
                              );
                            }
                            const older = s.latest.month !== scoreMonth;
                            return (
                              <button
                                type="button"
                                onClick={() => setScoreEmp({ code: r.employeeCode, name: r.employeeName })}
                                title={`${scoreMonthLabel(s.latest.month)} score · Click for the last 6 months`}
                                className="inline-block min-w-[3rem] cursor-pointer whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-semibold text-gray-800 hover:bg-gray-200"
                              >
                                {formatScore(s.latest.score)}
                                {older && <span className="font-normal text-gray-500"> · {scoreMonthShort(s.latest.month)}</span>}
                              </button>
                            );
                          })()}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCardWithStates>
          </div>
        </>
      )}

      {/* ── Per-parameter drill-down modal (clicked a red-flag cell) ── */}
      {detailSel && (
        <RedFlagDetailModal
          emp={detailSel.emp}
          flagType={detailSel.flagType}
          label={detailSel.label}
          month={data?.period?.label ?? `${fromDate} – ${toDate}`}
          pt={detailSel.pt}
          rule={detailSel.rule}
          data={detailData}
          loading={detailLoading}
          onClose={closeDetail}
        />
      )}

      {/* ── Employee Score drill-down: the last 6 months (clicked a score) ── */}
      {scoreEmp && scoreOf(scoreEmp.code) && (
        <EmployeeScoreHistoryModal
          emp={scoreEmp}
          summary={scoreOf(scoreEmp.code)!}
          onClose={() => setScoreEmp(null)}
        />
      )}

      {/* ── Red-flag audit modal: raised → highlighted → discussed (Done) + TAT ── */}
      {modalEmp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={closeTimeline}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <div>
                <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
                  <Flag className="h-4 w-4 text-red-600" />
                  Red-flag audit — {modalEmp.name}
                  {data?.period?.label && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500">
                      {data.period.label}
                    </span>
                  )}
                </div>
                <div className="font-mono text-[11px] text-gray-400">{modalEmp.code}</div>
              </div>
              <button
                onClick={closeTimeline}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[calc(85vh-7rem)] overflow-auto px-5 py-3">
              <p className="mb-3 text-xs text-gray-500">
                Each red flag raised for this employee{data?.period?.label ? ` in ${data.period.label}` : ''} — when it was
                highlighted to the AM, whether they marked it <span className="font-medium text-gray-700">Done</span>{' '}
                (discussed with the employee) in the app, and the turnaround (TAT) from highlight → Done. When the AM marks
                the employee's card Done, every flag still open at that moment shares that Done time.
                {(data?.period?.includesToday ?? false) && (
                  <span className="mt-1 block text-amber-600">
                    Today's just-raised flags enter this audit after tonight's run, so this can trail the live badge by today's new flags.
                  </span>
                )}
              </p>
              {timelineLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                  <InlineLoader label="Loading…" />
                </div>
              ) : timelineError ? (
                <div className="py-10 text-center text-sm text-red-600">{timelineError}</div>
              ) : !timeline || timeline.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">
                  No raised red flags on record for this employee{data?.period?.label ? ` in ${data.period.label}` : ''}.
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-400">
                      <th className="py-2 pr-3 font-medium">Red flag</th>
                      <th className="py-2 pr-3 font-medium">Highlighted to AM</th>
                      <th className="py-2 pr-3 font-medium">Discussed?</th>
                      <th className="py-2 pr-3 font-medium">Marked Done</th>
                      <th className="py-2 font-medium">TAT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {timeline.map((t, i) => (
                      <tr key={`${t.flagType}-${t.raisedDate}-${i}`} className="align-top">
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900">{t.label || fullLabel(t.flagType, rulesByKey.get(t.flagType))}</span>
                            {t.live && (
                              <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-blue-700">
                                Live
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-400">Seen {fmtDateOnly(t.raisedDate)}</div>
                        </td>
                        <td className="py-2 pr-3 text-gray-700">
                          {(() => {
                            const src = t.highlightSource || (t.pushedAt ? 'push' : 'deck');
                            const label =
                              src === 'push' ? 'push notification'
                                : src === 'digest' ? '8 AM digest'
                                : src === 'detected' ? 'detected'
                                : 'on the deck';
                            return (
                              <>
                                {src === 'deck'
                                  ? <div>{fmtDateOnly(t.raisedDate)}</div>
                                  : <div>{fmtDateTime(t.highlightedAt)}</div>}
                                <div className="text-[11px] text-gray-400">{label}</div>
                              </>
                            );
                          })()}
                        </td>
                        <td className="py-2 pr-3">
                          {t.done ? (
                            <span className="inline-block rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-semibold text-green-700">
                              Done
                            </span>
                          ) : (
                            <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-gray-700">{t.done ? fmtDateTime(t.doneAt) : '—'}</td>
                        <td className="py-2 font-semibold text-gray-900">{fmtTat(t.tatMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Appeals review modal: details + attachment + thread + decision ── */}
      {appealEmp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setAppealEmp(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div>
                <div className="text-base font-semibold text-gray-900">Appeals — {appealEmp.name}</div>
                <div className="font-mono text-[11px] text-gray-400">{appealEmp.code}</div>
              </div>
              <button onClick={() => setAppealEmp(null)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4">
              {(appealsByEmp.get(appealEmp.code) || []).length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">No appeals raised by this employee.</div>
              ) : (
                (appealsByEmp.get(appealEmp.code) || []).map((a) => {
                  const st = APPEAL_STATUS[a.status];
                  const busy = appealBusy === a.id;
                  return (
                    <div key={a.id} className="rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
                        <div>
                          <div className="font-medium text-gray-900">{fullLabel(a.flagType, rulesByKey.get(a.flagType))}</div>
                          <div className="text-[11px] text-gray-400">Flag on {fmtDateOnly(a.behaviourDate)} · raised {fmtDateTime(a.createdAt)}</div>
                        </div>
                        <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="space-y-3 px-4 py-3">
                        <div className="whitespace-pre-wrap rounded bg-gray-50 p-3 text-sm text-gray-800">{a.details}</div>
                        {a.hasAttachment && (
                          <button onClick={() => viewAppealDoc(a.id)} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                            📎 {a.attachmentName || 'View attachment'}
                          </button>
                        )}
                        {a.messages.length > 0 && (
                          <div className="space-y-2">
                            {a.messages.map((m) => (
                              <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.authorType === 'admin' ? 'bg-blue-50' : 'bg-gray-100'}`}>
                                <div className="mb-0.5 text-[11px] font-semibold text-gray-500">
                                  {m.authorType === 'admin' ? (m.authorName || 'Review team') : (a.employeeName || 'Employee')} · {fmtDateTime(m.createdAt)}
                                </div>
                                <div className="whitespace-pre-wrap text-gray-800">{m.body}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {a.status === 'cancelled' || a.status === 'rejected' ? (
                          <div className="text-[12px] text-gray-500">
                            Decided {fmtDateTime(a.decidedAt)}{a.decidedBy ? ` by ${a.decidedBy}` : ''}.
                          </div>
                        ) : (
                          <div className="space-y-2 border-t border-gray-100 pt-3">
                            <textarea
                              value={replyDrafts[a.id] || ''}
                              onChange={(e) => setReplyDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                              placeholder="Write a reply, or a note for your decision (optional)…"
                              rows={2}
                              className="w-full rounded border border-gray-200 px-3 py-2 text-sm"
                            />
                            <p className="text-[11px] text-gray-400">
                              This text is sent as a reply, or attached as the note when you Cancel / Reject.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                disabled={busy || !(replyDrafts[a.id] || '').trim()}
                                onClick={() => appealReply(a.id)}
                                className="rounded bg-gray-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40"
                              >
                                Send reply
                              </button>
                              <div className="flex-1" />
                              <button
                                disabled={busy}
                                onClick={() => decideAppeal(a, 'cancel')}
                                className="inline-flex items-center gap-1 rounded bg-green-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40"
                              >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Cancel Red Flag
                              </button>
                              <button
                                disabled={busy}
                                onClick={() => decideAppeal(a, 'reject')}
                                className="inline-flex items-center gap-1 rounded bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
                              >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reject Appeal
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
